import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Card,
  CardContent,
  Radio,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import {
  applyContactEnrichment,
  matchContactEnrichment,
} from "../services/contactService";

const defaultValues = {
  name: "",
  phoneNumber: "",
  email: "",
  company: "",
  secondaryContact: "",
  secondaryPhone: "",
  secondaryEmail: "",
  notes: "",
  callsMade: "",
  followUpCalls: "",
  emailsSent: "",
  dropIns: "",
  mergeMode: "fill_empty",
  createIfNoMatch: true,
};

const ContactEnrichmentDialog = ({ open, onClose, onApplied }) => {
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [applying, setApplying] = useState(false);

  const { control, handleSubmit, reset, getValues } = useForm({
    defaultValues,
  });

  const resetDialog = useCallback(() => {
    reset(defaultValues);
    setMatches([]);
    setSelectedMatch(null);
  }, [reset]);

  useEffect(() => {
    if (!open) {
      resetDialog();
    }
  }, [open, resetDialog]);

  const handleFindMatches = async () => {
    const values = getValues();
    setLoadingMatches(true);
    try {
      const result = await matchContactEnrichment(values);
      const nextMatches = result?.matches || [];
      setMatches(nextMatches);
      setSelectedMatch(nextMatches[0]?.contact?.id || null);
    } finally {
      setLoadingMatches(false);
    }
  };

  const onSubmit = async (data) => {
    setApplying(true);
    try {
      const response = await applyContactEnrichment({
        incoming: data,
        contactId: selectedMatch || undefined,
        mergeMode: data.mergeMode,
        createIfNoMatch: data.createIfNoMatch,
        appendNotes: true,
        counterMode: "add",
      });

      if (onApplied) {
        onApplied(response.contact);
      }
      onClose(false);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => onClose(false)}>
      <DialogTitle>Enrich Existing Contact</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth label="Name" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="company"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth label="Company" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth type="tel" label="Phone Number" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth label="Email" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="secondaryContact"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Secondary Contact" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="secondaryPhone"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Secondary Phone" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="secondaryEmail"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Secondary Email" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Merge Mode</InputLabel>
              <Controller
                name="mergeMode"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Merge Mode">
                    <MenuItem value="fill_empty">Fill Empty Fields</MenuItem>
                    <MenuItem value="prefer_incoming">Prefer Incoming</MenuItem>
                    <MenuItem value="overwrite">Overwrite Existing</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth multiline minRows={4} label="Notes" />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="callsMade"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Calls Made (+)" type="number" />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="followUpCalls"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Follow-Up Calls (+)"
                  type="number"
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="emailsSent"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Emails Sent (+)" type="number" />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="dropIns"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Drop-Ins (+)" type="number" />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="createIfNoMatch"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="Create a new contact if no reliable match is found"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Button onClick={handleFindMatches} variant="outlined" disabled={loadingMatches}>
              {loadingMatches ? "Finding..." : "Find Possible Matches"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            {loadingMatches ? (
              <CircularProgress size={20} />
            ) : matches.length ? (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Match Candidates
                </Typography>
                {matches.map((match) => (
                  <Card key={match.contact.id} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Grid container alignItems="center">
                        <Grid item xs={1}>
                          <Radio
                            checked={selectedMatch === match.contact.id}
                            onChange={() => setSelectedMatch(match.contact.id)}
                          />
                        </Grid>
                        <Grid item xs={11}>
                          <Typography variant="body1">
                            {match.contact.name} / {match.contact.company || "No company"}
                          </Typography>
                          <Typography variant="body2">
                            Score: {match.score} | Reasons: {match.reasons.join(", ")}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No candidates yet. Run a match search to review potential existing contacts.
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="error"
          variant="outlined"
          onClick={() => onClose(false)}
          disabled={applying}
        >
          Cancel
        </Button>
        <Button
          color="success"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={applying}
        >
          {applying ? "Applying..." : "Apply Enrichment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactEnrichmentDialog;
